/**
 * Mixin d'édition de stock (ligne à ligne + mode « Gérer les stocks »).
 *
 * Contrat hôte — exposer un computed `stockConfig` :
 * {
 *   key(item)            → identifiant stable (uuid) utilisé par le lot (`items[].uuid`)
 *   name(item)           → libellé pour les toasts
 *   value(item)          → stock courant (null = ∞)
 *   editable(item)       → la cellule est-elle éditable (permission + type)
 *   apply(item, value, serverPayload?) → applique une valeur à la ligne (optimiste ou retour serveur)
 *   singleUrl(item)      → URL du PATCH unitaire
 *   bulkUrl              → URL du POST en masse
 *   toBulkItem?(key, value) → entrée du payload bulk (défaut `{ uuid: key, stock: value }`)
 * }
 *
 * Le serveur reste la source de vérité : on affiche de manière optimiste puis on resynchronise
 * avec la réponse ; en erreur on restaure et on prévient.
 */
import { EventBus } from '../../../eventBus.js'
import { summarizeStockChanges, formatBulkSummary } from './stockUtils'

const FLASH_DURATION = 1200
const TOAST_OPTIONS = { timeout: 5000 }

export default {
    data() {
        return {
            stockEditMode: false,
            /** key → { from, to, name } */
            pendingStocks: {},
            editingStockKey: null,
            flashStockKeys: {},
            stockNote: '',
            isSavingStock: false,
            showStockLeaveDialog: false,
        }
    },
    computed: {
        pendingStockKeys() {
            return Object.keys(this.pendingStocks)
        },
        hasPendingStocks() {
            return this.pendingStockKeys.length > 0
        },
        pendingStockCount() {
            return this.pendingStockKeys.length
        },
        stockSummary() {
            return summarizeStockChanges(this.pendingStockKeys.map((key) => this.pendingStocks[key]))
        },
        stockSummaryLabel() {
            return formatBulkSummary(this.stockSummary, (key, params) => this.$t(key, params))
        },
    },
    methods: {
        // ── Lecture ──────────────────────────────────────────────────────────
        stockKeyOf(item) {
            return this.stockConfig.key(item)
        },
        stockValueOf(item) {
            const value = this.stockConfig.value(item)
            return value === undefined ? null : value
        },
        /** Brouillon courant (undefined si aucun brouillon pour la ligne). */
        stockDraftOf(item) {
            const pending = this.pendingStocks[this.stockKeyOf(item)]
            return pending ? pending.to : undefined
        },
        /** Mode lot : toutes les lignes éditables affichent le stepper ; sinon une seule ligne à la fois. */
        isStockEditing(item) {
            if (this.stockEditMode) return this.isStockEditable(item)
            return this.editingStockKey !== null && this.editingStockKey === this.stockKeyOf(item)
        },
        isStockEditable(item) {
            return !!this.stockConfig.editable(item)
        },
        stockRowClass(item) {
            return this.flashStockKeys[this.stockKeyOf(item)] ? 'stock-row-flash' : ''
        },

        // ── Édition d'une cellule ────────────────────────────────────────────
        startStockEdit(item) {
            if (this.stockEditMode || !this.isStockEditable(item) || this.isSavingStock) return
            this.editingStockKey = this.stockKeyOf(item)
        },
        updateStockDraft(item, value) {
            const key = this.stockKeyOf(item)
            const initial = this.stockValueOf(item)
            const next = value === undefined ? null : value
            if (next === initial) {
                if (this.pendingStocks[key]) this.$delete(this.pendingStocks, key)
                return
            }
            const existing = this.pendingStocks[key]
            this.$set(this.pendingStocks, key, {
                from: existing ? existing.from : initial,
                to: next,
                name: this.stockConfig.name(item),
            })
        },
        /** Esc : la ligne revient à sa valeur enregistrée (brouillon supprimé), dans les deux modes. */
        cancelStockEdit(item) {
            const key = this.stockKeyOf(item)
            if (this.pendingStocks[key]) this.$delete(this.pendingStocks, key)
            if (!this.stockEditMode) this.editingStockKey = null
        },
        /**
         * Enter / blur du stepper. Mode simple : enregistre (PATCH). Mode lot : fige le brouillon et passe
         * à la ligne suivante — jamais de PATCH (le stepper n'émet d'ailleurs pas `commit` au blur en lot).
         */
        onStockCommit(item, value) {
            if (this.stockEditMode) {
                this.updateStockDraft(item, value)
                this.focusSiblingStockInput(1)
                return
            }
            return this.commitSingleStock(item, value)
        },
        /** Tab / Shift+Tab en mode lot : saute directement au champ de la ligne voisine. */
        onStockNavigate({ direction, event }) {
            if (this.focusSiblingStockInput(direction) && event) event.preventDefault()
        },
        /** Donne le focus au champ de stock voisin du champ actif ; retourne false s'il n'y en a pas. */
        focusSiblingStockInput(direction) {
            if (!this.$el || typeof this.$el.querySelectorAll !== 'function') return false
            const inputs = Array.from(this.$el.querySelectorAll('.stock-stepper__input'))
            const index = inputs.indexOf(document.activeElement)
            if (index === -1) return false
            const next = inputs[index + direction]
            if (!next) return false
            next.focus()
            next.select()
            return true
        },
        async commitSingleStock(item, value) {
            const key = this.stockKeyOf(item)
            const previous = this.stockValueOf(item)
            const next = value === undefined ? null : value
            this.editingStockKey = null
            if (this.pendingStocks[key]) this.$delete(this.pendingStocks, key)
            if (next === previous) return // inchangé : aucun appel

            this.stockConfig.apply(item, next)
            this.isSavingStock = true
            try {
                const response = await axios.patch(this.stockConfig.singleUrl(item), { stock: next })
                const payload = response && response.data ? response.data : {}
                if (payload.food) {
                    const food = payload.food
                    this.stockConfig.apply(item, food.manage_stock ? Number(food.stock) : null, food)
                }
                this.$toast.success(this.$t('stock.toast.saved_single', { name: this.stockConfig.name(item) }), TOAST_OPTIONS)
                this.flashStockRows([key])
                this.$emit('refresh-stats')
            } catch (error) {
                this.stockConfig.apply(item, previous)
                this.$toast.error(this.stockErrorMessage(error), TOAST_OPTIONS)
            } finally {
                this.isSavingStock = false
            }
        },

        // ── Mode « Gérer les stocks » ────────────────────────────────────────
        enterStockEditMode() {
            // Une édition en cours est déjà dans `pendingStocks` (updateStockDraft à chaque frappe) :
            // elle devient un brouillon du lot. Toutes les lignes éditables passent en stepper ; le stepper
            // déjà ouvert garde son instance et perd seulement `commitOnBlur` → aucun PATCH.
            this.stockEditMode = true
            this.editingStockKey = null
        },
        exitStockEditMode() {
            this.stockEditMode = false
            this.pendingStocks = {}
            this.editingStockKey = null
            this.stockNote = ''
        },
        toggleStockEditMode() {
            if (this.stockEditMode) {
                this.requestLeaveStockEditMode()
            } else {
                this.enterStockEditMode()
            }
        },
        requestLeaveStockEditMode() {
            this.confirmLeave(() => this.exitStockEditMode())
        },
        /**
         * Garde de sortie générique. Appelle `proceed` immédiatement s'il n'y a rien à perdre
         * (retourne true), sinon ouvre la modale et retourne false ; `proceed` sera appelé si
         * l'utilisateur abandonne, `cancel` s'il continue l'édition.
         */
        confirmLeave(proceed, cancel = null) {
            if (!this.hasPendingStocks || this._stockForceExit) {
                this.exitStockEditMode()
                if (typeof proceed === 'function') proceed()
                return true
            }
            this._stockLeaveHandlers = { proceed, cancel }
            this.showStockLeaveDialog = true
            return false
        },
        onStockLeaveDiscard() {
            const handlers = this._stockLeaveHandlers || {}
            this._stockLeaveHandlers = null
            this.showStockLeaveDialog = false
            this.exitStockEditMode()
            if (typeof handlers.proceed === 'function') handlers.proceed()
        },
        onStockLeaveContinue() {
            const handlers = this._stockLeaveHandlers || {}
            this._stockLeaveHandlers = null
            this.showStockLeaveDialog = false
            if (typeof handlers.cancel === 'function') handlers.cancel()
        },
        async saveBulkStocks() {
            if (!this.hasPendingStocks || this.isSavingStock) return
            const keys = [...this.pendingStockKeys]
            const toBulkItem = this.stockConfig.toBulkItem || ((key, value) => ({ uuid: key, stock: value }))
            const items = keys.map((key) => toBulkItem(key, this.pendingStocks[key].to))
            const note = (this.stockNote || '').trim()
            const payload = { items }
            if (note) payload.note = note

            this.isSavingStock = true
            try {
                const response = await axios.post(this.stockConfig.bulkUrl, payload)
                const result = response && response.data ? response.data : {}
                // Lignes encore affichées : on applique le brouillon (le serveur a la même valeur)
                const rows = Array.isArray(this.items) ? this.items : []
                rows.forEach((row) => {
                    const pending = this.pendingStocks[this.stockKeyOf(row)]
                    if (pending) this.stockConfig.apply(row, pending.to)
                })
                const count = typeof result.updated === 'number' ? result.updated : keys.length
                let message = this.$t(count > 1 ? 'stock.toast.saved_bulk_other' : 'stock.toast.saved_bulk_one', { count })
                if (note) message += ` · ${this.$t('stock.toast.note_added')}`
                this.$toast.success(message, TOAST_OPTIONS)
                this.exitStockEditMode()
                this.flashStockRows(keys)
                this.$emit('refresh-stats')
            } catch (error) {
                // On reste en mode édition, brouillons intacts
                this.$toast.error(this.stockErrorMessage(error), TOAST_OPTIONS)
            } finally {
                this.isSavingStock = false
            }
        },

        // ── Divers ───────────────────────────────────────────────────────────
        flashStockRows(keys) {
            keys.forEach((key) => this.$set(this.flashStockKeys, key, true))
            setTimeout(() => {
                keys.forEach((key) => this.$delete(this.flashStockKeys, key))
            }, FLASH_DURATION)
        },
        stockErrorMessage(error) {
            const data = error && error.response && error.response.data
            if (data) {
                if (data.message) return data.message
                if (data.errors) {
                    const first = Object.values(data.errors).flat()[0]
                    if (first) return first
                }
            }
            return this.$t('stock.toast.error')
        },

        // ── Gardes de navigation (actives seulement avec des brouillons) ────
        _stockHasUnsaved() {
            return !this._stockForceExit && this.hasPendingStocks
        },
        _stockHandleRequestNavigation(navEvent) {
            if (this._stockHasUnsaved()) navEvent.defaultPrevented = true
        },
        _stockHandleForceNavigation() {
            this._stockForceExit = true
            if (this._stockBeforeUnloadHandler) {
                window.removeEventListener('beforeunload', this._stockBeforeUnloadHandler)
            }
        },
    },
    mounted() {
        this._stockForceExit = false

        this._stockBeforeUnloadHandler = (e) => {
            if (this._stockHasUnsaved()) {
                e.preventDefault()
                e.returnValue = ''
            }
        }
        window.addEventListener('beforeunload', this._stockBeforeUnloadHandler)

        this._stockTurboBeforeVisitHandler = (e) => {
            if (!this._stockHasUnsaved()) return
            e.preventDefault()
            const url = e.detail && e.detail.url
            this.confirmLeave(() => {
                this._stockHandleForceNavigation()
                if (!url) return
                if (window.Turbo) {
                    window.Turbo.visit(url)
                } else {
                    window.location.href = url
                }
            })
        }
        document.addEventListener('turbo:before-visit', this._stockTurboBeforeVisitHandler)

        EventBus.$on('request-navigation', this._stockHandleRequestNavigation)
        EventBus.$on('force-navigation', this._stockHandleForceNavigation)
    },
    beforeDestroy() {
        if (this._stockBeforeUnloadHandler) {
            window.removeEventListener('beforeunload', this._stockBeforeUnloadHandler)
        }
        if (this._stockTurboBeforeVisitHandler) {
            document.removeEventListener('turbo:before-visit', this._stockTurboBeforeVisitHandler)
        }
        EventBus.$off('request-navigation', this._stockHandleRequestNavigation)
        EventBus.$off('force-navigation', this._stockHandleForceNavigation)
    },
}
